import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { IndianRupee, Percent, Wallet, Package, Truck, ArrowRight, Zap } from "lucide-react";
import { api, inr, apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";

const GST_LABEL = { verified: "GST Verified", pending: "GST Pending", failed: "GST Failed", none: "No GST" };

const DONE = ["Delivered", "Cancelled", "Returned", "Refunded"];

export default function SellerDashboard() {
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [upOpen, setUpOpen] = useState(false);
  const [gstin, setGstin] = useState("");
  const [upBusy, setUpBusy] = useState(false);
  const loadMe = () => api.get("/seller/me").then((r) => setMe(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/seller/commission").then((r) => setC(r.data)).catch(() => {});
    loadMe();
    api.get("/seller/suborders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  const upgrade = async () => {
    if (gstin.trim().length < 15) { toast.error("15-digit GSTIN daalein"); return; }
    setUpBusy(true);
    try { const { data } = await api.post("/seller/upgrade-online", { gstin: gstin.trim().toUpperCase() }); toast.success(data.message); setUpOpen(false); setGstin(""); loadMe(); }
    catch (e) { toast.error(apiErr(e)); } finally { setUpBusy(false); }
  };

  const active = orders.filter((o) => !DONE.includes(o.status)).length;
  const payout = c?.payout || {};

  return (
    <div className="max-w-5xl mx-auto" data-testid="seller-dashboard">
      <h1 className="text-2xl font-bold mb-1">Seller Dashboard</h1>
      <p className="text-slate-500 text-sm mb-5">Aapki sales, commission aur orders ek jagah.</p>

      {me && (
        <div data-testid="seller-status-card" className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
          <span data-testid="seller-mode-badge" className={`text-xs font-extrabold px-2.5 py-1 rounded-full ${me.online_selling_enabled ? "bg-blue-100 text-blue-700" : me.selling_mode === "online" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
            {me.online_selling_enabled ? "ONLINE SELLING — ACTIVE" : me.selling_mode === "online" ? "ONLINE — PENDING APPROVAL" : "CATALOG ONLY"}
          </span>
          <span data-testid="seller-gst-badge" className={`text-xs font-bold px-2.5 py-1 rounded-full ${me.gst_status === "verified" ? "bg-emerald-100 text-emerald-700" : me.gst_status === "pending" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
            {GST_LABEL[me.gst_status] || "No GST"}
          </span>
          {me.gst_legal_name && <span className="text-xs text-slate-500 truncate max-w-[220px]">{me.gst_legal_name}</span>}
          {!me.online_selling_enabled && ((me.gst_status === "verified" || me.gst_status === "pending") ? (
            <span data-testid="seller-online-waiting" className="ml-auto text-xs text-amber-600 font-semibold">⏳ Online selling owner approval ka intezaar</span>
          ) : (
            <button data-testid="upgrade-online-btn" onClick={() => setUpOpen(true)} className="ml-auto px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold text-sm flex items-center gap-1.5"><Zap size={14} /> Upgrade to Online Selling</button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="kpi-gross" icon={IndianRupee} label="Gross Sales" value={inr(c?.gross_sales || 0)} tone="slate" />
        <Kpi testid="kpi-commission" icon={Percent} label="Platform Commission" value={inr(c?.total_commission || 0)} tone="rose" />
        <Kpi testid="kpi-net" icon={Wallet} label="Net Earnings" value={inr(c?.net_payable || 0)} tone="emerald" />
        <Kpi testid="kpi-available" icon={Wallet} label="Available Payout" value={inr(payout.available || 0)} tone="orange" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
        <Kpi testid="kpi-products" icon={Package} label="Products" value={me?.product_count ?? "—"} tone="blue" onClick={() => nav("/seller/products")} />
        <Kpi testid="kpi-active-orders" icon={Truck} label="Active Orders" value={active} tone="amber" onClick={() => nav("/seller/orders")} />
        <Kpi testid="kpi-paid" icon={Wallet} label="Paid Out" value={inr(payout.paid_out || 0)} tone="slate" />
        <Kpi testid="kpi-hold" icon={Wallet} label="In Hold (T+N)" value={inr(payout.pending || 0)} tone="slate" />
      </div>

      {payout.awaiting_approval > 0 && (
        <div data-testid="dash-awaiting" className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          ⏳ {inr(payout.awaiting_approval)} owner approval ka intezaar kar raha hai (self-delivered orders). Approve hone ke baad payout available hoga.
        </div>
      )}

      <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold">Recent Orders</h2>
          <button data-testid="dash-all-orders" onClick={() => nav("/seller/orders")} className="text-sm font-semibold text-orange-600 flex items-center gap-1">All orders <ArrowRight size={14} /></button>
        </div>
        {orders.length === 0 ? (
          <p className="text-slate-400 text-sm py-6 text-center" data-testid="dash-no-orders">Abhi tak koi order nahi aaya.</p>
        ) : (
          <div className="divide-y divide-slate-50">
            {orders.slice(0, 6).map((o) => (
              <div key={o.id} data-testid={`dash-order-${o.id}`} className="flex items-center justify-between py-2.5">
                <div className="min-w-0"><p className="font-semibold text-sm truncate">{o.suborder_no}</p><p className="text-[11px] text-slate-400">{o.customer_name || "Customer"} · {inr(o.subtotal)}</p></div>
                <StatusBadge status={o.status} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 bg-white rounded-2xl border border-slate-100 p-4">
        <h2 className="font-bold mb-3">Recent Commission</h2>
        {(c?.rows || []).length === 0 ? (
          <p className="text-slate-400 text-sm py-6 text-center" data-testid="dash-no-commission">Delivered orders par commission yahan dikhega.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase text-slate-400"><th className="py-1.5">Order</th><th>Product</th><th className="text-right">Gross</th><th className="text-right">Net</th><th className="text-right">Status</th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {c.rows.slice(0, 8).map((r) => (
                  <tr key={r.id} data-testid={`dash-comm-${r.id}`}>
                    <td className="py-2 text-slate-500">{r.order_no}</td>
                    <td className="truncate max-w-[140px]">{r.product_name} ×{r.qty}</td>
                    <td className="text-right">{inr(r.gross)}</td>
                    <td className="text-right font-semibold">{inr(r.net_payable)}</td>
                    <td className="text-right">{r.status === "reversed" ? <span className="text-rose-500 text-xs font-bold">Reversed</span> : r.owner_approved === false ? <span className="text-amber-600 text-xs font-bold">Approval</span> : r.settled ? <span className="text-slate-400 text-xs">Paid</span> : <span className="text-emerald-600 text-xs font-bold">Accrued</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={upOpen} onOpenChange={(v) => !v && setUpOpen(false)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle>Upgrade to Online Selling</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">GST verify hone ke baad owner approval milte hi aapke products online orders ke liye live ho jayenge.</p>
          <input data-testid="upgrade-gstin-input" placeholder="15-digit GSTIN" value={gstin} maxLength={15} onChange={(e) => setGstin(e.target.value.toUpperCase())} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm tracking-wider" />
          <button data-testid="upgrade-gstin-submit" onClick={upgrade} disabled={upBusy} className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white rounded-xl font-bold text-sm">{upBusy ? "Verifying…" : "Verify GST & Request"}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TONE = {
  slate: "text-slate-900", rose: "text-rose-600", emerald: "text-emerald-600",
  orange: "text-orange-600", blue: "text-blue-600", amber: "text-amber-600",
};
const Kpi = ({ testid, icon: Icon, label, value, tone, onClick }) => (
  <div data-testid={testid} onClick={onClick} className={`bg-white rounded-2xl border border-slate-100 p-4 ${onClick ? "cursor-pointer hover:border-slate-300 transition-colors" : ""}`}>
    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wide"><Icon size={13} /> {label}</div>
    <p className={`text-xl font-extrabold mt-1 ${TONE[tone]}`}>{value}</p>
  </div>
);
